import type { ChatItem } from '../../../contracts/harness.types';
import { isEvidenceSubagentToolName } from '../components/EvidenceSessionInlineBlock.utils';

export interface TurnLifecycle {
  lastItem: ChatItem | undefined;
  /** Last `user_message` id, reverse scan. */
  latestUserMessageId: string | null;
  /** Settled `assistant_text` | terminal `tool_use`. */
  latestTurnHasReply: boolean;
  /** Tail `assistant_text` already painting words. */
  latestReplyHasText: boolean;
  /** `turn_cancelled_marker` | `turn_failed` | `prompt_context_failed`. */
  latestTurnIsTerminal: boolean;
}

const getLatestTurnHasReply = (item: ChatItem | undefined): boolean =>
  item?.kind === 'assistant_text'
    ? !item.streaming || (item.text?.trim().length ?? 0) > 0
    : item?.kind === 'tool_use'
      ? item.status === 'complete' ||
        item.status === 'error' ||
        item.status === 'cancelled'
      : false;

const getLatestReplyHasText = (item: ChatItem | undefined): boolean =>
  item?.kind === 'assistant_text' && (item.text?.trim().length ?? 0) > 0;

const getLatestUserMessageId = (items: ChatItem[]): string | null => {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (item?.kind === 'user_message') return item.id;
  }
  return null;
};

export interface ProjectTurnGroup {
  key: string;
  items: ChatItem[];
}

/** Splits the timeline into user-initiated turns: by ``turnId`` first, else
 *  at each visible user message. Items before the first turn are leading. */
export const groupProjectTurns = (
  items: readonly ChatItem[],
  isVisibleUserMessage: (item: ChatItem, isFirstUserMessage: boolean) => boolean
): { leadingItems: ChatItem[]; turnGroups: ProjectTurnGroup[] } => {
  const firstUserMessageId = items.find((entry) => entry.kind === 'user_message')?.id;
  const leadingItems: ChatItem[] = [];
  const turnGroups: ProjectTurnGroup[] = [];
  let currentTurnGroup: ProjectTurnGroup | null = null;
  let currentTurnId: string | undefined;

  for (const item of items) {
    const turnId = item.kind === 'prompt_context_failed' ? undefined : item.turnId;
    const sameTurn = turnId !== undefined && turnId === currentTurnId;
    const switchesTurn =
      currentTurnGroup !== null &&
      currentTurnId !== undefined &&
      turnId !== undefined &&
      turnId !== currentTurnId;
    const opensByUserMessage =
      !sameTurn &&
      item.kind === 'user_message' &&
      isVisibleUserMessage(item, item.id === firstUserMessageId);

    if (switchesTurn || opensByUserMessage) {
      currentTurnId = turnId;
      currentTurnGroup = { key: turnId ?? item.id, items: [item] };
      turnGroups.push(currentTurnGroup);
      continue;
    }

    if (currentTurnGroup) {
      currentTurnGroup.items.push(item);
      if (currentTurnId === undefined && turnId !== undefined) {
        currentTurnId = turnId;
        currentTurnGroup.key = turnId;
      }
    } else {
      leadingItems.push(item);
    }
  }

  return { leadingItems, turnGroups };
};

export const findTrailingEvidenceChildToolCallId = (
  items: readonly ChatItem[]
): string | null => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (
      item?.kind === 'thinking' ||
      (item?.kind === 'assistant_text' && !item.text?.trim())
    ) {
      continue;
    }
    return item?.kind === 'tool_use' &&
      item.status === 'complete' &&
      item.childAgentRunId &&
      isEvidenceSubagentToolName(item.name)
      ? item.id
      : null;
  }
  return null;
};

/** Derives every "what state is the latest turn in" predicate in one pass. */
export const deriveTurnLifecycle = (items: ChatItem[]): TurnLifecycle => {
  const lastItem = items.at(-1);
  return {
    lastItem,
    latestUserMessageId: getLatestUserMessageId(items),
    latestTurnHasReply: getLatestTurnHasReply(lastItem),
    latestReplyHasText: getLatestReplyHasText(lastItem),
    latestTurnIsTerminal:
      lastItem?.kind === 'turn_cancelled_marker' ||
      lastItem?.kind === 'turn_failed' ||
      lastItem?.kind === 'prompt_context_failed',
  };
};

export interface TurnActivityFlags {
  turnInFlight: boolean;
  isSubmitting: boolean;
  hasQueuedTurns: boolean;
  runFailed: boolean;
  runClosed: boolean;
  hasPendingApproval: boolean;
  inFlightDesktopCall: boolean;
  isStartingSeededTurn: boolean;
  isPreviewingUserMessage: boolean;
  /** A live Working section already owns the progress chrome. */
  hasLiveWorkingSection: boolean;
}

export interface ChatBusyInputs {
  turnInFlight: boolean;
  isSubmitting: boolean;
  hasQueuedTurns?: boolean;
}

/** A queued turn counts as busy, so a turn boundary never reads as idle. */
export const deriveChatBusy = ({
  turnInFlight,
  isSubmitting,
  hasQueuedTurns = false,
}: ChatBusyInputs): boolean => turnInFlight || isSubmitting || hasQueuedTurns;

export const deriveShowThinking = (
  lifecycle: TurnLifecycle,
  flags: TurnActivityFlags
): boolean =>
  (flags.isStartingSeededTurn ||
    flags.isPreviewingUserMessage ||
    (deriveChatBusy(flags) && !lifecycle.latestReplyHasText)) &&
  !flags.runFailed &&
  !flags.runClosed &&
  !flags.inFlightDesktopCall &&
  !flags.hasPendingApproval &&
  !flags.hasLiveWorkingSection;

export const deriveLatestTurnNeedsRoomLive = (
  lifecycle: TurnLifecycle,
  flags: TurnActivityFlags
): boolean =>
  !flags.runFailed &&
  !flags.runClosed &&
  (flags.isPreviewingUserMessage ||
    flags.turnInFlight ||
    flags.isSubmitting ||
    (!lifecycle.latestTurnHasReply && !lifecycle.latestTurnIsTerminal));
